<template>
    <div class="card">

        <div class="grid">
            <div class="form-field col flex justify-content-left flex-wrap mb-2 align-items-center">
                User selected:&nbsp;
                <span class="userSelected">{{props.user.name}}</span>&nbsp;
                <span class="userSelected">{{props.user.surname}}</span>
            </div>
            <div class="form-field col flex justify-content-left flex-wrap mb-2 align-items-center">
                <label for="assessment-date" class="mr-2 font-bold">Assessment Date:</label>
                <Calendar :manualInput="true" id="assessment-date" dateFormat="dd-mm-yy" v-model="assessment_date" :showIcon="true" />
            </div>
        </div>

        <Divider />
        <!--  Shows step title in each step, and shows predefined text at the end of the step  -->
        <div v-if="!loading" class="text-center text-2xl mb-3 font-semibold">
            {{ currentStep?.label === 'user_block' ? 'User Profile Details' : currentStep?.label ?? 'Assessment Completed' }}
            <div class="text-base my-3 mx-2">
                {{ currentStep?.description }}
            </div>
        </div>

        <!-- Step's Form Attributes -->
        <!-- whenever its loading in assessmentForm state, then show loader -->
        <div v-if="loading">
            <ScaleLoader class="loader" />
            <Skeleton class="mb-2 w-full" height="10rem" borderRadius="16px" />
        </div>
        <div v-else class="form-attributes">

            <div v-if="!getFormAttributes && !finalSubmission">
                <div class="text-center text-xl">You are now at the end of the assessment. Would you like to review your submitted information before final submission?</div>
            </div>
            <div v-else class="field" v-for="attribute in getFormAttributes" :key="attribute.id">
                <div v-if="getAttributeInputType(attribute.type) == AttributeInputTypes.HEADING">
                    <h2 v-if="!attribute.label">{{ 'HEADING' }}</h2>
                    <h2 v-else>{{ attribute.label }}</h2>
                </div>
                <div v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.SUB_HEADING">
                    <h5 v-if="!attribute.label">{{ 'SUB HEADING' }}</h5>
                    <h5 v-else>{{ attribute.label }}</h5>
                </div>
                <div v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.STATIC_TEXT">
                    <span v-if="!attribute.label">{{ 'FREE TEXT' }}</span>
                    <span v-else>{{ attribute.label }}</span>
                </div>

                <label v-else :for="attribute.label">
                    {{ attribute.label }}
                    <span v-if="isMandatory(attribute)" class="mandatory"> *</span>
                </label> <span v-if="attribute.unit && attribute.unit.length > 0" class="superscript">({{ attribute.unit }})</span>
                <div class="p-inputgroup">
                    <InputText
                        maxlength = "255"
                        :id="attribute.label"
                        v-if="getAttributeInputType(attribute.type) == AttributeInputTypes.INPUT_TEXT"
                        v-model="formData['a_' + attribute.id]"
                        :placeholder="attribute.placeholder"
                        :required="attribute.required"
                        @blur="() => $nextTick(() => checkMandatoryField(attribute, formData['a_' + attribute.id]))"
                        @focus="cleanMandatoryField(attribute)"
                        :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                    />

                    <InputNumber
                        :id="attribute.label"
                        v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.NUMBER"
                        v-model="formData['a_' + attribute.id]"
                        :placeholder="attribute.placeholder"
                        :useGrouping="false"
                        :suffix="attribute.suffix"
                        :minFractionDigits="2"
                        :maxFractionDigits="2"
                        :required="attribute.required"
                        @blur="() => $nextTick(() => checkMandatoryField(attribute, formData['a_' + attribute.id]))"
                        @focus="cleanMandatoryField(attribute)"
                        :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                    />

                    <Textarea
                        :id="attribute.label"
                        v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.LONG_INPUT_TEXT"
                        v-model="formData['a_' + attribute.id]"
                        rows="2"
                        maxlength="255"
                        :required="attribute.required"
                        :placeholder="attribute.placeholder"
                        @blur="checkMandatoryField(attribute, formData['a_' + attribute.id])"
                        @focus="cleanMandatoryField(attribute)"
                        :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                    />
                    <Calendar
                        manualInput
                        @input="updateCalendarData(attribute.id, $event)"
                        :id="attribute.label"
                        v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.DATE"
                        dateFormat="dd-mm-yy"
                        v-model="formData['a_' + attribute.id]"
                        :showIcon="true"
                        :required="attribute.required"
                        placeholder="dd-mm-yy"
                        v-tooltip="'Format: dd-mm-yyyy'"
                        @blur="checkMandatoryField(attribute, formData['a_' + attribute.id])"
                        @focus="cleanMandatoryField(attribute)"
                        @date-select="cleanMandatoryField(attribute)"
                        :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                    />

                    <Dropdown
                        :id="attribute.label"
                        v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.DROP_DOWN"
                        v-model="formData['a_' + attribute.id]"
                        :options="attribute.options"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="attribute.placeholder"
                        :required="attribute.required"
                        showClear
                        @blur="checkMandatoryField(attribute, formData['a_' + attribute.id])"
                        @focus="cleanMandatoryField(attribute)"
                        :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                    />
                    <MultiSelect
                        :id="attribute.label"
                        v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.MULTI_DROP_DOWN"
                        v-model="formData['a_' + attribute.id]"
                        :options="attribute.options"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="attribute.placeholder"
                        :required="attribute.required"
                        showClear
                        @blur="checkMandatoryField(attribute, formData['a_' + attribute.id])"
                        @focus="cleanMandatoryField(attribute)"
                        :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                    />
                    <duration-input-type v-else-if="getAttributeInputType(attribute.type) == AttributeInputTypes.DURATION"
                                         :id="attribute.id"
                                         @duration-set="durationInputs"
                                         :modelValue="formData['a_' + attribute.id]"
                                         @blur="checkMandatoryField(attribute, formData['a_' + attribute.id])"
                                         @focus="cleanMandatoryField(attribute)"
                                         :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                    />
                    <div v-else class="flex flex-column justify-content-center gap-3">
                        <div v-for="option in attribute.options" :key="option.id" class="flex align-items-center">
                            <Checkbox
                                :id="option.value"
                                v-if="getAttributeInputType(attribute.type) == AttributeInputTypes.CHECKBOX"
                                v-model="formData['a_' + attribute.id]"
                                :name="option.value"
                                :value="option.value"
                                @blur="checkMandatoryField(attribute, formData['a_' + attribute.id])"
                                @focus="cleanMandatoryField(attribute)"
                                :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                            />
                            <RadioButton
                                :id="option.value"
                                v-else
                                v-model="formData['a_' + attribute.id]"
                                :name="option.value"
                                :value="option.value"
                                @blur="checkMandatoryField(attribute, formData['a_' + attribute.id])"
                                @focus="cleanMandatoryField(attribute)"
                                :class="{ 'p-invalid': invalidAttributeIds?.includes(attribute.id) }"
                            />
                            <label :for="option.value" class="ml-2">{{ option.label }}</label>
                        </div>
                    </div>
                </div>
                <!-- display error messages -->
                <small class="p-invalid" :for="attribute.label" v-if="invalidAttributeIds?.includes(attribute.id)">{{ formStore.getValidationErrorMessage(attribute.id) }}</small>
            </div>
        </div>

        <div class="flex justify-content-between" v-if="!invalidAttributeIds || invalidAttributeIds.length === 0">
            <div>
                <Button v-if="finalSubmission" class="p-button-warning mr-2" label="Start new assessment" v-tooltip="'Begin a new assessment'" @click="startNewAssessment" />
                <Button v-else-if="hasPreviousStep" label="Previous" class="p-button p-button-warning" v-tooltip="'Go Previous Step'" @click="formStore.goPreviousStep()" :disabled="finalSubmission" />
            </div>
            <div>
                <Button v-if="finalSubmission" label="View assessments" v-tooltip="'View all submitted assessments'" @click="goToAnswers" />
                <Button v-else :label="!currentStep ? 'Submit' : 'Next'" @click="submit" v-tooltip="'Submit Current Step'" :disabled="finalSubmission" />
            </div>
            <!-- Preview will only be shown in the end of the submission when the currentStep value is null -->
            <div v-if="!currentStep">
                <Button label="Review" class="p-button p-button-success" @click="previewDialog = true" />
            </div>
        </div>
        <div v-else class="errorWarning">
            There are <b>{{invalidAttributeIds.length}}</b> field<span v-if="invalidAttributeIds.length > 1">s</span>
            that <span v-if="invalidAttributeIds.length > 1">are</span><span v-else>is</span> required. <br />
            Complete <span v-if="invalidAttributeIds.length > 1">them</span><span v-else>it</span> to proceed.
        </div>
        <preview-submission-answer v-if="previewDialog" @hide-dialog="previewDialog = false" />
    </div>
</template>

<!-- At the moment, this component is only submitting normal assessments and its still not applicable with user profile assessments -->
<script setup>
import PreviewSubmissionAnswer from './PreviewSubmissionAnswer.vue';
import DurationInputType from '@/components/input-type/DurationInputType.vue';

import { ref, computed, inject, watch, defineProps } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';

import { AttributeInputTypes } from '@/enums';
import { getAttributeInputType } from '@/helpers/enum-helpers';
import { showToast } from '@/helpers/toast-handler';


const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
})

const router = useRouter();

const answerStore = inject('answerStore');
const formStore = inject('formStore');
const userId = inject('userId');

// Store States
const { userProfileAnswer } = storeToRefs(answerStore);
const { loading, assessment_date, currentStep, currentStepAnswer, finalSubmission, hasPreviousStep, invalidAttributeIds } = storeToRefs(formStore);

// this variable is used to collect all the dynamic form attributes values entered by a user in real-time,
// during submission it will be formated and mapped accordingly
const formData = ref([]);
const previewDialog = ref(false);

// Watchers
watch(
    () => userProfileAnswer.value,
    () => loadUserProfileAnswers(),
    {
        deep: true,
    }
);

// generates the form attributes list of current step
const getFormAttributes = computed(() => {
    return currentStep.value?.block?.form_attributes;
});

// Actions
const loadUserProfileAnswers = () => {
    // now load the userProfileAnswer into the formData
    userProfileAnswer?.value?.forEach((answerObj) => {
        formData.value['a_' + answerObj.attribute_id] = answerObj.answer;
    });
};

const submit = async () => {
    // while submitting in the initial step the user_id and form_id in the currentStepAnswer will always be '' by default
    // check if its null and if yes the put the user_id value
    if (currentStepAnswer.value?.user_id == '') {
        currentStepAnswer.value.user_id = userId;
    }
    // if there are answers to be submitted then map them accordingly with the helper from the store
    if (currentStepAnswer.value.answers) {
        //map formData into answers
        await formStore.formatCurrentStepAnswers(formData.value);
    }
    try {

        if (typeof getFormAttributes.value !== 'undefined') {
            getFormAttributes.value.forEach((attribute) => {
                checkMandatoryField(attribute)
            })
        }

        if (invalidAttributeIds.value.length === 0) {
            // in case there are no answers key in the currentStepAnswer then ignore it and just submit the currentStep
            // submit the step data
            await formStore.submitCurrentStep(currentStepAnswer.value);
            handleRedirection();
        }

    } catch (error) {
        console.log('error', error)
        showToast({}, 'Failed to submit assessment');
    }
};

// there is issues with duration at the moment
// whenever duration inputs are set, it will trigger this function and load the duration value to its respective keys inside the answers
const durationInputs = (obj) => {
    formData.value['a_' + obj.id] = obj.value;
};
// update manual calendar inputs
const updateCalendarData = (attributeId, event) => {
    formData['a_' + attributeId] = event.target.value;

    // remove errors
    invalidAttributeIds.value = invalidAttributeIds.value.filter(function(item) {
        return item !== attributeId
    })
};

// redirect users to start a new assessment
const startNewAssessment = () => {
    return router.push({ name: 'BeginAssessment' });
};

const goToAnswers = () => {
    return router.push({ name: 'AssessmentAnswers' });
};

const handleRedirection = () => {
  // check for the currentStep code, incase if its a user_block then redirect back to where he/she came from
  if (currentStep.value?.code === 'user_block') {
    setTimeout(() => {
      router.push({ name: 'UserSpecificAnswersTable', params: { userId } });
    }, 2000);
  }
};

function isMandatory(attribute) {
    if (attribute === null || typeof attribute === 'undefined') {
        return false
    }

    return attribute.required === 1 || attribute.required === '1'
}

function checkMandatoryField(attribute, inputField) {

    invalidAttributeIds.value = invalidAttributeIds.value.filter(function(item) {
        return item !== attribute.id
    })

    if (typeof inputField === 'undefined' && (('a_'+attribute.id) in formData.value)) {
        inputField = formData.value[ 'a_'+attribute.id ]
    }

    if (isMandatory(attribute)) {
        if (typeof inputField === 'undefined' || inputField.length === 0) {
            invalidAttributeIds.value.push(attribute.id)
        }
    }
}

function cleanMandatoryField(attribute) {
    invalidAttributeIds.value = invalidAttributeIds.value.filter(function(item) {
        return item !== attribute.id
    })
}

</script>

<style scoped>
.loader {
    position: relative;
    top: 10vh;
    right: 40vw;
    z-index: 1;
}
.form-attributes {
    margin: 2rem;
    cursor: pointer;
}
.superscript {
    position: relative;
    top: -0.1em;
    font-size: 80%;
}

.mandatory {
    position: relative;
    top: -0.1em;
    font-size: 80%;
    color: rgb(255, 82, 82);
}

.errorWarning {
    border: 1px solid rgb(255, 82, 82);
    margin: 2em;
    padding: 1em;
}

.userSelected {
    text-transform: capitalize;
    color: rgb(19, 90, 169);
}

</style>
